import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Register from './pages/auth/Register';
import Login from './pages/auth/Login';
import Profile from './pages/auth/Profile';
import Dashboard from './pages/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import SubscriptionRoute from './components/SubscriptionRoute';
import { ThemeModeProvider } from './contexts/ThemeContext';

// Pages
import Home from './pages/Home';
import SpeedReader from './pages/SpeedReader';
import Subscription from './pages/Subscription';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import AffiliateProgram from './pages/AffiliateProgram';
import Features from './pages/Features';
import About from './pages/About';
import SpeedTest from './pages/SpeedTest';
import Achievements from './pages/Achievements';
import Leaderboard from './pages/Leaderboard';
import AdminPanel from './pages/AdminPanel';
import Vocabulary from './pages/Vocabulary';
import TrainableSkill from './pages/blog/TrainableSkill';
import GuideDot from './pages/blog/GuideDot';
import BlogHome from './pages/blog/BlogHome';
import Chunking from './pages/blog/Chunking';
import Subvocalization from './pages/blog/Subvocalization';
import ComprehensionVsSpeed from './pages/blog/ComprehensionVsSpeed';
import DailyRoutine from './pages/blog/DailyRoutine';
import EyeFixation from './pages/blog/EyeFixation';
import ScreenVsPaper from './pages/blog/ScreenVsPaper';
import SpeedReadingMyths from './pages/blog/SpeedReadingMyths';
import SpacedRepetition from './pages/blog/SpacedRepetition';
import BeginnersGuide from './pages/blog/BeginnersGuide';
import ReadMoreBooks from './pages/blog/ReadMoreBooks';
import RereadingParagraph from './pages/blog/RereadingParagraph';
import TechnicalReading from './pages/blog/TechnicalReading';
import ReadingSpeedByAge from './pages/blog/ReadingSpeedByAge';
import SpeedReadingADHD from './pages/blog/SpeedReadingADHD';
import TripleSpeed30Days from './pages/blog/TripleSpeed30Days';
import ReadForeignLanguage from './pages/blog/ReadForeignLanguage';
import RememberEverything from './pages/blog/RememberEverything';
import WhySoSlow from './pages/blog/WhySoSlow';
import FocusWhileReading from './pages/blog/FocusWhileReading';
import ReadFasterForExams from './pages/blog/ReadFasterForExams';
import ReadSameLineOverAndOver from './pages/blog/ReadSameLineOverAndOver';
import DoesSpeedReadingWork from './pages/blog/DoesSpeedReadingWork';
import ImproveVocabularyReading from './pages/blog/ImproveVocabularyReading';
import ComprehensionAdults from './pages/blog/ComprehensionAdults';
import BookAWeek from './pages/blog/BookAWeek';
import SpeedReadingVsSkimming from './pages/blog/SpeedReadingVsSkimming';
import ReadWithoutGettingTired from './pages/blog/ReadWithoutGettingTired';
import ReadingStamina from './pages/blog/ReadingStamina';
import EyesSkipLines from './pages/blog/EyesSkipLines';
import ZoningOut from './pages/blog/ZoningOut';
import Read100Pages from './pages/blog/Read100Pages';
import ForgetWhatIRead from './pages/blog/ForgetWhatIRead';
import BackIntoReading from './pages/blog/BackIntoReading';
import StopMovingLips from './pages/blog/StopMovingLips';
import GoodReadingSpeed from './pages/blog/GoodReadingSpeed';
import ReadTextbooks from './pages/blog/ReadTextbooks';
import BestReadingPosition from './pages/blog/BestReadingPosition';
import ReadBeforeBed from './pages/blog/ReadBeforeBed';
import ReadingExercises from './pages/blog/ReadingExercises';
import DifficultBooks from './pages/blog/DifficultBooks';
import BenefitsReadingDaily from './pages/blog/BenefitsReadingDaily';
import PeripheralVision from './pages/blog/PeripheralVision';
import AnnotateBooks from './pages/blog/AnnotateBooks';
import TimeToRead200Pages from './pages/blog/TimeToRead200Pages';
import ReadResearchPaper from './pages/blog/ReadResearchPaper';
import ConcentrateLongHours from './pages/blog/ConcentrateLongHours';
import EyeExercisesReading from './pages/blog/EyeExercisesReading';
import Read50BooksYear from './pages/blog/Read50BooksYear';
import ReadNonfiction from './pages/blog/ReadNonfiction';
import ReadingFluency from './pages/blog/ReadingFluency';
import MakeReadingFun from './pages/blog/MakeReadingFun';
import WhyReadingBoring from './pages/blog/WhyReadingBoring';
import EnjoyReadingAgain from './pages/blog/EnjoyReadingAgain';
import ReadForPleasure from './pages/blog/ReadForPleasure';
import WhyPeopleNotRead from './pages/blog/WhyPeopleNotRead';
import FindBooksYouLike from './pages/blog/FindBooksYouLike';
import BenefitsReadingFun from './pages/blog/BenefitsReadingFun';
import BuildReadingHabit from './pages/blog/BuildReadingHabit';
import ReadingVsSocialMedia from './pages/blog/ReadingVsSocialMedia';
import GenresForPeopleWhoHateReading from './pages/blog/GenresForPeopleWhoHateReading';
import ReadWhenNoTime from './pages/blog/ReadWhenNoTime';
import WhyReadingFeelsGood from './pages/blog/WhyReadingFeelsGood';
import StartBookClub from './pages/blog/StartBookClub';
import ReadingForStressRelief from './pages/blog/ReadingForStressRelief';
import StopScrollingStartReading from './pages/blog/StopScrollingStartReading';
import AudiobooksVsReading from './pages/blog/AudiobooksVsReading';
import HowReadingChangesYourBrain from './pages/blog/HowReadingChangesYourBrain';
import EasyBooksForAdults from './pages/blog/EasyBooksForAdults';
import SetReadingGoals from './pages/blog/SetReadingGoals';
import MeasureReadingSpeed from './pages/blog/MeasureReadingSpeed';
import HowLongToLearnSpeedReading from './pages/blog/HowLongToLearnSpeedReading';
import CanAdultsSpeedRead from './pages/blog/CanAdultsSpeedRead';
import ReadFasterAtWork from './pages/blog/ReadFasterAtWork';
import BenefitsOfReadingFaster from './pages/blog/BenefitsOfReadingFaster';
import HowFastCanBrainRead from './pages/blog/HowFastCanBrainRead';
import HowToTakeNotesWhileReading from './pages/blog/HowToTakeNotesWhileReading';
import PreReadingStrategies from './pages/blog/PreReadingStrategies';
import HowToReadCritically from './pages/blog/HowToReadCritically';
import HowToSummarizeABook from './pages/blog/HowToSummarizeABook';
import TeachBackMethodReading from './pages/blog/TeachBackMethodReading';
import HowToSynthesizeMultipleBooks from './pages/blog/HowToSynthesizeMultipleBooks';
import ActiveReadingStrategies from './pages/blog/ActiveReadingStrategies';
import AverageReadingSpeed from './pages/blog/AverageReadingSpeed';
import ReadABookEveryWeek from './pages/blog/ReadABookEveryWeek';
import ReadFasterWithoutLosingComprehension from './pages/blog/ReadFasterWithoutLosingComprehension';
import StudyTextbookFaster from './pages/blog/StudyTextbookFaster';
import ReadOnPhone from './pages/blog/ReadOnPhone';
import ReadingForPleasure from './pages/blog/ReadingForPleasure';
import WhyDoesReadingMakeYouSleepy from './pages/blog/WhyDoesReadingMakeYouSleepy';
import RebuildAttentionSpan from './pages/blog/RebuildAttentionSpan';
import HowToReadBoringMaterial from './pages/blog/HowToReadBoringMaterial';
import ReadFasterCollege from './pages/blog/ReadFasterCollege';
import SpeedReadingKindle from './pages/blog/SpeedReadingKindle';
import ReadMultipleBooksAtOnce from './pages/blog/ReadMultipleBooksAtOnce';
import PerfectReadingEnvironment from './pages/blog/PerfectReadingEnvironment';
import IncreaseReadingSpeed from './pages/blog/IncreaseReadingSpeed';
import BestSpeedReadingApps from './pages/blog/BestSpeedReadingApps';
import ReadBookInOneDay from './pages/blog/ReadBookInOneDay';
import AverageBooksPerYear from './pages/blog/AverageBooksPerYear';
import MorningReadingRoutine from './pages/blog/MorningReadingRoutine';
import BionicReading from './pages/blog/BionicReading';
import BestTimeToRead from './pages/blog/BestTimeToRead';
import HowToFinishABook from './pages/blog/HowToFinishABook';
import PomodoroReading from './pages/blog/PomodoroReading';
import ReadFasterOnScreen from './pages/blog/ReadFasterOnScreen';
import HowLongToRead100Pages from './pages/blog/HowLongToRead100Pages';
import ReadingSpeedTest from './pages/blog/ReadingSpeedTest';
import StayAwakeWhileReading from './pages/blog/StayAwakeWhileReading';
import ReadFasterRememberEverything from './pages/blog/ReadFasterRememberEverything';
import AnnotateTextbook from './pages/blog/AnnotateTextbook';
import TimeToRead300Pages from './pages/blog/TimeToRead300Pages';
import PagesPerHour from './pages/blog/PagesPerHour';
import SpeedReadingExercises from './pages/blog/SpeedReadingExercises';
import DailyReadingHabit from './pages/blog/DailyReadingHabit';
import TextbookInOneDay from './pages/blog/TextbookInOneDay';
import TimeToRead150Pages from './pages/blog/TimeToRead150Pages';
import TimeToRead400Pages from './pages/blog/TimeToRead400Pages';
import ReadingTipsForAdults from './pages/blog/ReadingTipsForAdults';
import RememberWhatYouReadExams from './pages/blog/RememberWhatYouReadExams';
import WhyIsMyReadingComprehensionSoBad from './pages/blog/WhyIsMyReadingComprehensionSoBad';
import TimeToRead50Pages from './pages/blog/TimeToRead50Pages';
import BestSpeedReadingTools from './pages/blog/BestSpeedReadingTools';
import TimeToRead250Pages from './pages/blog/TimeToRead250Pages';
import TimeToRead500Pages from './pages/blog/TimeToRead500Pages';
import TimeToReadAPage from './pages/blog/TimeToReadAPage';
import ReadingComprehensionExercises from './pages/blog/ReadingComprehensionExercises';
import SpeedReadingForStudents from './pages/blog/SpeedReadingForStudents';
import AreAudiobooksFasterThanReading from './pages/blog/AreAudiobooksFasterThanReading';
import HowManyBooksShouldYouRead from './pages/blog/HowManyBooksShouldYouRead';
import ReduceEyeStrainWhileReading from './pages/blog/ReduceEyeStrainWhileReading';
import WhyReadingFeelsHard from './pages/blog/WhyReadingFeelsHard';
import DoesReadingMakeYouSmarter from './pages/blog/DoesReadingMakeYouSmarter';
import Is300WpmGood from './pages/blog/Is300WpmGood';
import WhatReadingSpeedSaysAboutYou from './pages/blog/WhatReadingSpeedSaysAboutYou';
import HowToImproveWpm from './pages/blog/HowToImproveWpm';
import SlowReaderVsFastReader from './pages/blog/SlowReaderVsFastReader';
import InstagramLanding from './pages/InstagramLanding';
import EbookLanding from './pages/EbookLanding';
import EbookThankYou from './pages/EbookThankYou';
import EmailPreferences from './pages/EmailPreferences';
import ReadingBlueprint from './pages/ReadingBlueprint';
import Books from './pages/Books';
import ReadFasterWithoutForgettingEverything from './pages/blog/ReadFasterWithoutForgettingEverything';
import Review from './pages/Review';
import ReviewRedirect from './pages/ReviewRedirect';
import ShortLinkRedirect from './pages/ShortLinkRedirect';
import FlexCardLanding from './pages/FlexCardLanding';
import Challenge from './pages/Challenge';
import FlexProfile from './pages/FlexProfile';
import FlexLeaderboard from './pages/FlexLeaderboard';
import FlexFeed from './pages/FlexFeed';
import FlexGroups from './pages/FlexGroups';

// Components
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';

// Lazy-load the new SpeedTestV2 for code splitting
const SpeedTestV2 = React.lazy(() => import('./pages/SpeedTestV2'));

// Smart affiliate route: logged-in users go to their dashboard, logged-out users see terms
const AffiliateRoute = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/profile?tab=1" replace /> : <AffiliateProgram />;
};

function App() {
  return (
    <ThemeModeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Public Routes */}
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Home />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              <Route path="/books" element={<Books />} />
              <Route path="/books/read-faster-without-forgetting-everything" element={<ReadFasterWithoutForgettingEverything />} />
              <Route path="/review" element={<Review />} />
              <Route path="/review/:slug" element={<ReviewRedirect />} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms" element={<TermsOfService />} />
              <Route path="/affiliate" element={<AffiliateRoute />} />
              <Route path="/speed-test" element={
                process.env.REACT_APP_SPEED_TEST_V2 === 'true' ? (
                  <Suspense fallback={<div style={{ minHeight: '50vh' }} />}>
                    <SpeedTestV2 />
                  </Suspense>
                ) : (
                  <SpeedTest />
                )
              } />
              <Route path="/blog" element={<BlogHome />} />
              <Route path="/blog/reading-speed-is-a-trainable-skill" element={<TrainableSkill />} />
              <Route path="/blog/guide-dot-speed-reading" element={<GuideDot />} />
              <Route path="/blog/chunking-read-phrases-not-words" element={<Chunking />} />
              <Route path="/blog/stop-subvocalizing-read-faster" element={<Subvocalization />} />
              <Route path="/blog/reading-speed-vs-comprehension" element={<ComprehensionVsSpeed />} />
              <Route path="/blog/15-minute-speed-reading-routine" element={<DailyRoutine />} />
              <Route path="/blog/eye-fixation-how-eyes-read" element={<EyeFixation />} />
              <Route path="/blog/digital-reading-screens-vs-paper" element={<ScreenVsPaper />} />
              <Route path="/blog/speed-reading-myths" element={<SpeedReadingMyths />} />
              <Route path="/blog/spaced-repetition-vocabulary-reading" element={<SpacedRepetition />} />
              <Route path="/blog/how-to-read-faster-beginners-guide" element={<BeginnersGuide />} />
              <Route path="/blog/how-to-read-more-books" element={<ReadMoreBooks />} />
              <Route path="/blog/why-you-keep-rereading-same-paragraph" element={<RereadingParagraph />} />
              <Route path="/blog/speed-read-academic-papers-technical-documents" element={<TechnicalReading />} />
              <Route path="/blog/reading-speed-by-age" element={<ReadingSpeedByAge />} />
              <Route path="/blog/speed-reading-adhd" element={<SpeedReadingADHD />} />
              <Route path="/blog/triple-reading-speed-30-days" element={<TripleSpeed30Days />} />
              <Route path="/blog/read-foreign-language-without-translating" element={<ReadForeignLanguage />} />
              <Route path="/blog/how-to-remember-everything-you-read" element={<RememberEverything />} />
              <Route path="/blog/why-do-i-read-so-slow" element={<WhySoSlow />} />
              <Route path="/blog/how-to-focus-while-reading" element={<FocusWhileReading />} />
              <Route path="/blog/how-to-read-faster-for-exams" element={<ReadFasterForExams />} />
              <Route path="/blog/why-do-i-read-the-same-line-over-and-over" element={<ReadSameLineOverAndOver />} />
              <Route path="/blog/does-speed-reading-actually-work" element={<DoesSpeedReadingWork />} />
              <Route path="/blog/best-way-to-improve-vocabulary-while-reading" element={<ImproveVocabularyReading />} />
              <Route path="/blog/improve-reading-comprehension-adults" element={<ComprehensionAdults />} />
              <Route path="/blog/how-to-read-a-book-a-week" element={<BookAWeek />} />
              <Route path="/blog/speed-reading-vs-skimming" element={<SpeedReadingVsSkimming />} />
              <Route path="/blog/how-to-read-without-getting-tired" element={<ReadWithoutGettingTired />} />
              <Route path="/blog/increase-reading-stamina" element={<ReadingStamina />} />
              <Route path="/blog/why-do-my-eyes-skip-lines-when-reading" element={<EyesSkipLines />} />
              <Route path="/blog/how-to-stop-zoning-out-while-reading" element={<ZoningOut />} />
              <Route path="/blog/how-to-read-100-pages-in-an-hour" element={<Read100Pages />} />
              <Route path="/blog/why-do-i-forget-what-i-read" element={<ForgetWhatIRead />} />
              <Route path="/blog/how-to-get-back-into-reading" element={<BackIntoReading />} />
              <Route path="/blog/how-to-stop-moving-lips-when-reading" element={<StopMovingLips />} />
              <Route path="/blog/what-is-a-good-reading-speed" element={<GoodReadingSpeed />} />
              <Route path="/blog/how-to-read-textbooks-effectively" element={<ReadTextbooks />} />
              <Route path="/blog/best-reading-position" element={<BestReadingPosition />} />
              <Route path="/blog/how-to-read-before-bed-without-falling-asleep" element={<ReadBeforeBed />} />
              <Route path="/blog/reading-exercises-for-adults" element={<ReadingExercises />} />
              <Route path="/blog/how-to-read-difficult-books" element={<DifficultBooks />} />
              <Route path="/blog/benefits-of-reading-every-day" element={<BenefitsReadingDaily />} />
              <Route path="/blog/how-to-use-peripheral-vision-to-read-faster" element={<PeripheralVision />} />
              <Route path="/blog/how-to-annotate-a-book" element={<AnnotateBooks />} />
              <Route path="/blog/how-long-does-it-take-to-read-200-pages" element={<TimeToRead200Pages />} />
              <Route path="/blog/how-to-read-a-research-paper" element={<ReadResearchPaper />} />
              <Route path="/blog/how-to-concentrate-on-reading-for-long-hours" element={<ConcentrateLongHours />} />
              <Route path="/blog/eye-exercises-for-reading" element={<EyeExercisesReading />} />
              <Route path="/blog/how-to-read-50-books-a-year" element={<Read50BooksYear />} />
              <Route path="/blog/how-to-read-nonfiction-books-effectively" element={<ReadNonfiction />} />
              <Route path="/blog/how-to-improve-reading-fluency-adults" element={<ReadingFluency />} />
              <Route path="/blog/how-to-make-reading-fun" element={<MakeReadingFun />} />
              <Route path="/blog/why-is-reading-so-boring" element={<WhyReadingBoring />} />
              <Route path="/blog/how-to-enjoy-reading-again" element={<EnjoyReadingAgain />} />
              <Route path="/blog/how-to-read-for-pleasure" element={<ReadForPleasure />} />
              <Route path="/blog/why-do-people-not-read-anymore" element={<WhyPeopleNotRead />} />
              <Route path="/blog/how-to-find-books-you-like" element={<FindBooksYouLike />} />
              <Route path="/blog/benefits-of-reading-for-fun" element={<BenefitsReadingFun />} />
              <Route path="/blog/how-to-build-a-reading-habit" element={<BuildReadingHabit />} />
              <Route path="/blog/why-reading-is-better-than-social-media" element={<ReadingVsSocialMedia />} />
              <Route path="/blog/best-genres-for-people-who-hate-reading" element={<GenresForPeopleWhoHateReading />} />
              <Route path="/blog/how-to-read-when-you-have-no-time" element={<ReadWhenNoTime />} />
              <Route path="/blog/why-reading-feels-good" element={<WhyReadingFeelsGood />} />
              <Route path="/blog/how-to-start-a-book-club" element={<StartBookClub />} />
              <Route path="/blog/reading-for-stress-relief" element={<ReadingForStressRelief />} />
              <Route path="/blog/how-to-stop-scrolling-and-start-reading" element={<StopScrollingStartReading />} />
              <Route path="/blog/audiobooks-vs-reading" element={<AudiobooksVsReading />} />
              <Route path="/blog/how-reading-changes-your-brain" element={<HowReadingChangesYourBrain />} />
              <Route path="/blog/best-easy-books-for-adults-who-dont-read" element={<EasyBooksForAdults />} />
              <Route path="/blog/how-to-set-reading-goals" element={<SetReadingGoals />} />
              <Route path="/blog/how-to-measure-your-reading-speed" element={<MeasureReadingSpeed />} />
              <Route path="/blog/how-long-to-learn-speed-reading" element={<HowLongToLearnSpeedReading />} />
              <Route path="/blog/can-adults-learn-to-speed-read" element={<CanAdultsSpeedRead />} />
              <Route path="/blog/how-to-read-faster-at-work" element={<ReadFasterAtWork />} />
              <Route path="/blog/benefits-of-reading-faster" element={<BenefitsOfReadingFaster />} />
              <Route path="/blog/how-fast-can-the-human-brain-read" element={<HowFastCanBrainRead />} />
              <Route path="/blog/how-to-take-notes-while-reading" element={<HowToTakeNotesWhileReading />} />
              <Route path="/blog/pre-reading-strategies-for-comprehension" element={<PreReadingStrategies />} />
              <Route path="/blog/how-to-read-critically" element={<HowToReadCritically />} />
              <Route path="/blog/how-to-summarize-a-book" element={<HowToSummarizeABook />} />
              <Route path="/blog/teach-back-method-reading-retention" element={<TeachBackMethodReading />} />
              <Route path="/blog/how-to-synthesize-multiple-books" element={<HowToSynthesizeMultipleBooks />} />
              <Route path="/blog/active-reading-strategies" element={<ActiveReadingStrategies />} />
              <Route path="/blog/average-reading-speed" element={<AverageReadingSpeed />} />
              <Route path="/blog/read-a-book-every-week" element={<ReadABookEveryWeek />} />
              <Route path="/blog/read-faster-without-losing-comprehension" element={<ReadFasterWithoutLosingComprehension />} />
              <Route path="/blog/how-to-study-a-textbook-faster" element={<StudyTextbookFaster />} />
              <Route path="/blog/how-to-read-on-your-phone" element={<ReadOnPhone />} />
              <Route path="/blog/reading-for-pleasure" element={<ReadingForPleasure />} />
              <Route path="/blog/why-does-reading-make-you-sleepy" element={<WhyDoesReadingMakeYouSleepy />} />
              <Route path="/blog/rebuild-attention-span-for-reading" element={<RebuildAttentionSpan />} />
              <Route path="/blog/how-to-read-boring-material" element={<HowToReadBoringMaterial />} />
              <Route path="/blog/how-to-read-faster-college" element={<ReadFasterCollege />} />
              <Route path="/blog/speed-reading-kindle" element={<SpeedReadingKindle />} />
              <Route path="/blog/how-to-read-multiple-books-at-once" element={<ReadMultipleBooksAtOnce />} />
              <Route path="/blog/perfect-reading-environment" element={<PerfectReadingEnvironment />} />
              <Route path="/blog/how-to-increase-reading-speed" element={<IncreaseReadingSpeed />} />
              <Route path="/blog/best-speed-reading-apps" element={<BestSpeedReadingApps />} />
              <Route path="/blog/how-to-read-a-book-in-one-day" element={<ReadBookInOneDay />} />
              <Route path="/blog/how-many-books-does-the-average-person-read" element={<AverageBooksPerYear />} />
              <Route path="/blog/morning-reading-routine" element={<MorningReadingRoutine />} />
              <Route path="/blog/bionic-reading" element={<BionicReading />} />
              <Route path="/blog/best-time-of-day-to-read" element={<BestTimeToRead />} />
              <Route path="/blog/how-to-finish-a-book" element={<HowToFinishABook />} />
              <Route path="/blog/pomodoro-technique-for-reading" element={<PomodoroReading />} />
              <Route path="/blog/how-to-read-faster-on-screen" element={<ReadFasterOnScreen />} />
              <Route path="/blog/how-long-does-it-take-to-read-100-pages" element={<HowLongToRead100Pages />} />
              <Route path="/blog/reading-speed-test" element={<ReadingSpeedTest />} />
              <Route path="/blog/how-to-stay-awake-while-reading" element={<StayAwakeWhileReading />} />
              <Route path="/blog/how-to-read-faster-and-remember-everything" element={<ReadFasterRememberEverything />} />
              <Route path="/blog/how-to-annotate-a-textbook" element={<AnnotateTextbook />} />
              <Route path="/blog/how-long-does-it-take-to-read-300-pages" element={<TimeToRead300Pages />} />
              <Route path="/blog/how-many-pages-can-you-read-in-an-hour" element={<PagesPerHour />} />
              <Route path="/blog/speed-reading-exercises-for-beginners" element={<SpeedReadingExercises />} />
              <Route path="/blog/how-to-build-a-daily-reading-habit" element={<DailyReadingHabit />} />
              <Route path="/blog/how-to-read-a-textbook-in-one-day" element={<TextbookInOneDay />} />
              <Route path="/blog/how-long-does-it-take-to-read-150-pages" element={<TimeToRead150Pages />} />
              <Route path="/blog/how-long-does-it-take-to-read-400-pages" element={<TimeToRead400Pages />} />
              <Route path="/blog/reading-tips-for-adults" element={<ReadingTipsForAdults />} />
              <Route path="/blog/how-to-remember-what-you-read-for-exams" element={<RememberWhatYouReadExams />} />
              <Route path="/blog/why-is-my-reading-comprehension-so-bad" element={<WhyIsMyReadingComprehensionSoBad />} />
              <Route path="/blog/how-long-does-it-take-to-read-50-pages" element={<TimeToRead50Pages />} />
              <Route path="/blog/best-speed-reading-tools" element={<BestSpeedReadingTools />} />
              <Route path="/blog/how-long-does-it-take-to-read-250-pages" element={<TimeToRead250Pages />} />
              <Route path="/blog/how-long-does-it-take-to-read-500-pages" element={<TimeToRead500Pages />} />
              <Route path="/blog/how-long-does-it-take-to-read-a-page" element={<TimeToReadAPage />} />
              <Route path="/blog/reading-comprehension-exercises-for-adults" element={<ReadingComprehensionExercises />} />
              <Route path="/blog/speed-reading-for-students" element={<SpeedReadingForStudents />} />
              <Route path="/blog/are-audiobooks-faster-than-reading" element={<AreAudiobooksFasterThanReading />} />
              <Route path="/blog/how-many-books-should-you-read-a-year" element={<HowManyBooksShouldYouRead />} />
              <Route path="/blog/how-to-reduce-eye-strain-while-reading" element={<ReduceEyeStrainWhileReading />} />
              <Route path="/blog/why-reading-feels-hard" element={<WhyReadingFeelsHard />} />
              <Route path="/blog/does-reading-make-you-smarter" element={<DoesReadingMakeYouSmarter />} />
              <Route path="/blog/is-300-wpm-good" element={<Is300WpmGood />} />
              <Route path="/blog/what-your-reading-speed-says-about-you" element={<WhatReadingSpeedSaysAboutYou />} />
              <Route path="/blog/how-to-improve-words-per-minute" element={<HowToImproveWpm />} />
              <Route path="/blog/slow-reader-vs-fast-reader" element={<SlowReaderVsFastReader />} />
              
              {/* Authentication-only Routes */}
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              
              {/* Premium Features (Require Subscription) */}
              <Route
                path="/reader"
                element={
                  <SubscriptionRoute>
                    <SpeedReader />
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <SubscriptionRoute>
                    <Dashboard />
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/achievements"
                element={
                  <SubscriptionRoute>
                    <Achievements />
                  </SubscriptionRoute>
                }
              />
              <Route path="/leaderboard" element={<Navigate to="/leaderboards" replace />} />
              <Route path="/vocabulary" element={
                  <SubscriptionRoute>
                      <Vocabulary />
                  </SubscriptionRoute>
              } />
              <Route
                path="/internal/ctrl-9x7k"
                element={
                  <PrivateRoute>
                    <AdminPanel />
                  </PrivateRoute>
                }
              />
              
              {/* Instagram Landing Page */}
              <Route path="/Instagram" element={<InstagramLanding />} />
              <Route path="/instagram" element={<Navigate to="/Instagram" replace />} />

              {/* Ebook Companion Landing Page */}
              <Route path="/ebook" element={<EbookLanding />} />
              <Route path="/ebook/thank-you" element={<EbookThankYou />} />

              <Route path="/email-preferences" element={<EmailPreferences />} />

              {/* Reading Blueprint (token-based, no auth required) */}
              <Route path="/reading-blueprint/:token" element={<ReadingBlueprint />} />

              {/* Short Link Redirect */}
              <Route path="/s/:code" element={<ShortLinkRedirect />} />

              {/* FLEX Card Landing */}
              <Route path="/flex/card/:cardId" element={<FlexCardLanding />} />

              {/* FLEX Public Profile */}
              <Route path="/flex/:handle" element={<FlexProfile />} />

              {/* FLEX Leaderboards */}
              <Route path="/leaderboards" element={<FlexLeaderboard />} />

              {/* FLEX Feed */}
              <Route path="/feed" element={<FlexFeed />} />

              {/* FLEX Group Challenges */}
              <Route path="/groups" element={<FlexGroups />} />

              {/* Challenge */}
              <Route path="/challenge/:challengeId" element={<Challenge />} />

              {/* Redirect root to dashboard or login */}
              <Route
                path="/"
                element={<Navigate to="/dashboard" replace />}
              />
            </Routes>
          </Layout>
        </Router>
      </AuthProvider>
    </ThemeModeProvider>
  );
}

export default App;
